import type { TRecommendationsModalElement } from '@shared/dream-components';
import { ProprietaryRecommendationsModalElement, RecommendationsModalElement } from '@shared/dream-components';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';
import { ClientOnly } from 'remix-utils/client-only';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { RecommendationsModalContext, RecommendationsModalForm } from '~/components/RecommendationsModalAction';

import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { RootLoaderData } from '~/root';

interface Props {
  node: TRecommendationsModalElement;
  children: ReactNode | ReactNode[];
}

const RecommendationsModal = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const { handleCloseModal, currentModal } = useGlobalModals();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  const { nextStep, nextPage } = useSignupRedirects();

  const onOpenChange = (value: boolean) => {
    if (!value) {
      if (nextStep) {
        nextPage();
      } else {
        handleCloseModal({ preventScrollReset: true });
      }
    }
  };

  return (
    <ClientOnly fallback={null}>
      {() =>
        site.use_custom_recommendations_modal ? (
          <RecommendationsModalElement
            element={node}
            isOpen={currentModal === 'recommendations'}
            onOpenChange={onOpenChange}
          >
            {children}
          </RecommendationsModalElement>
        ) : (
          <RecommendationsModalContext onOpenChange={onOpenChange}>
            {({ email, isOpen, isLoading, recommendations, submittedIds, onSubmitSuccess }) => (
              <ProprietaryRecommendationsModalElement
                email={email}
                isLoading={isLoading}
                recommendations={recommendations}
                submittedIds={submittedIds}
                onSubmitSuccess={onSubmitSuccess}
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                theme={site.primary_theme}
                translations={{
                  'recommendations.modal.thank_you_title': t('recommendations.modal.thank_you_title'),
                  'recommendations.modal.recommended_title': t('recommendations.modal.recommended_title'),
                  'recommendations.modal.thank_you_subtitle': t('recommendations.modal.thank_you_subtitle'),
                  'recommendations.modal.recommended_subtitle': t('recommendations.modal.recommended_subtitle'),
                  'recommendations.modal.next': t('recommendations.modal.next'),
                  'recommendations.modal.skip': t('recommendations.modal.skip'),
                  'recommendations.modal.subscribe_to_all': t('recommendations.modal.subscribe_to_all'),
                  'recommendations.modal.subscribe': t('recommendations.modal.subscribe'),
                  'recommendations.modal.subscribed': t('recommendations.modal.subscribed'),
                  'recommendations.modal.subscribing': t('recommendations.modal.subscribing'),
                  'recommendations.modal.sponsored': t('recommendations.modal.sponsored'),
                }}
                SubscribeForm={RecommendationsModalForm}
              />
            )}
          </RecommendationsModalContext>
        )
      }
    </ClientOnly>
  );
};

export default RecommendationsModal;
